// src/pages/ExerciseHistory.jsx
import React, { useEffect, useState } from "react";
import { Lesson, User } from "@/api/entities";

function buildUserExercisesPrefix(u) {
  // Prefix corretto su R2 (senza "rb-media/" davanti)
  const raw = u?.id || u?.uid || u?.email || "anon";
  let safe = String(raw).replace(/[^a-z0-9_-]/gi, "");
  safe = safe.replace(/^user_/, "");
  return `users/user_${safe}/exercises/`;
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    if (!isNaN(d)) {
      return d.toLocaleString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {}
  // fallback se il nome file non è un ISO standard
  return iso.replace("T", " ").replace("Z", "");
}

export default function ExerciseHistory() {
  const [loading, setLoading] = useState(true);
  const [byLesson, setByLesson] = useState({});
  const [titleMap, setTitleMap] = useState({});
  const [orderMap, setOrderMap] = useState({});
  const [openLesson, setOpenLesson] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1) utente + prefix corretto
        const u = await User.me();
        const prefix = buildUserExercisesPrefix(u);

        // 2) list R2 (supporta {keys}, {objects}, {items})
        const url = `/api/r2-list?prefix=${encodeURIComponent(prefix)}`;
        const res = await fetch(url);
        const payload = await res.json().catch(() => ({}));

        let keys = [];
        if (Array.isArray(payload?.keys)) {
          keys = payload.keys;
        } else if (Array.isArray(payload?.objects)) {
          keys = payload.objects.map((o) => o.key);
        } else if (Array.isArray(payload?.items)) {
          keys = payload.items.map((o) => o.key);
        }

        // 3) raggruppa per lessonId (senza "lesson_")
        const grouped = {};
        for (const k of keys) {
          const m = k.match(/\/lesson_(\d+[^/]*?)\/([^/]+)\.json$/i);
          if (!m) continue;
          const lessonId = m[1]; // es: 1758897266431_j6arzb
          const fileIso = m[2];  // es: 2025-11-15T09-23-30-983Z oppure ISO standard
          (grouped[lessonId] ??= []).push({ iso: fileIso, key: k });
        }
        // ordina le date per ogni lezione
        for (const lid of Object.keys(grouped)) {
          grouped[lid].sort((a, b) => a.iso.localeCompare(b.iso));
        }

        // 4) catalogo lezioni → mappa {id senza "lesson_": "Lezione N: Titolo"} + ordine
        const lessons = await Lesson.list("order").catch(() => []);
        const tMap = {};
        const oMap = {};
        for (const l of lessons || []) {
          const normalizedId = String(l.id).replace(/^lesson_/, "");
          const numero =
            l.order !== null && l.order !== undefined && l.order !== ""
              ? `Lezione ${l.order}: `
              : "";
          tMap[normalizedId] = `${numero}${l.title || l.slug || ""}`;
          oMap[normalizedId] =
            typeof l.order === "number"
              ? l.order
              : Number(String(l.order || "").replace(/[^\d-]/g, "")) || 9999;
        }

        setByLesson(grouped);
        setTitleMap(tMap);
        setOrderMap(oMap);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Rileggi Esercizi</h1>
        <p>Caricamento…</p>
      </div>
    );
  }

  const lessonIds = Object.keys(byLesson);
  if (!lessonIds.length) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Rileggi Esercizi</h1>
        <p>Non hai ancora compilato nessun esercizio.</p>
      </div>
    );
  }

  // ordina blocchi per numero di lezione (fallback alfabetico)
  lessonIds.sort((a, b) => {
    const oa = orderMap[a] ?? 9999;
    const ob = orderMap[b] ?? 9999;
    if (oa !== ob) return oa - ob;
    return String(a).localeCompare(String(b));
    // eslint-disable-next-line
  });

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Rileggi Esercizi</h1>

      <div className="space-y-3">
        {lessonIds.map((lessonId) => {
          const entries = byLesson[lessonId] || [];
          const title = titleMap[lessonId] || `Lezione ${lessonId}`;
          const opened = openLesson === lessonId;

          return (
            <div
              key={lessonId}
              className="rounded-xl border bg-white shadow-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3">
                <div className="font-medium">{title}</div>
                <button
                  className="text-sm text-blue-600 hover:underline"
                  onClick={() => setOpenLesson(opened ? null : lessonId)}
                >
                  {opened ? "Nascondi date" : `Vedi date (${entries.length})`}
                </button>
              </div>

              {opened && (
                <div className="border-t px-4 py-3 space-y-2">
                  {entries.map((e) => {
                    const nice = fmtDate(e.iso);
                    const viewerUrl =
                      "/exercise-view?key=" + encodeURIComponent(e.key);
                    return (
                      <div
                        key={e.key}
                        className="flex items-center justify-between"
                      >
                        <div className="text-sm text-gray-700">{nice}</div>
                        <a
                          className="text-sm text-blue-600 hover:underline"
                          href={viewerUrl}
                        >
                          Apri
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
